import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { DeskTable, ElementTable } from '../../components/nucleus/Shelf';
import { Plus } from 'lucide-react';
import NucleusPage, { Verb } from '../../components/nucleus/NucleusPage';
import { useFacets, Faceted } from '../../components/nucleus/FacetRail';
import { Cell } from '../../components/nucleus/Collection';
import { Empty } from '../../components/nucleus/Empty';
import { Note, useOpen, useLinkedItem } from '../../components/nucleus/Inspector';
import { RealmCell, RealmRings } from '../../components/nucleus/cells';
import { SealedMark } from '../../components/nucleus/ContractMark';
import { EntityFace } from '../nucleus/RealmsParts';
import { useToast } from '../../components/common';
import api from '../../services/api';
import { usePlace } from '../../nucleus/place';
import AccessSection from '../access/AccessSection';
import { OLD_PAGES } from '../access/accessData';
import { KINDS } from '../nucleus/RealmsModel';
import RealmsEditor from '../nucleus/RealmsEditor';
import RealmInspector, { realmActionsOf, reachActionsOf } from './RealmInspector';
import { NameModal } from './RealmsModals';
import { useRealmsData, realmsApi, bases, recipientOf } from './realmsData';
import { plural } from '../../nucleus/format';
import './realms.css';

// PRIVACY & ACCESS — two pages: the exposure profiles (each connection's
// grant, in Access) and the privacy realms they are shared from (here). An old
// page address (/realms/apps, /realms/ended, /realms/health …) lands on the
// exposure profiles filtered the way that page was.
//
// ONE READ FOR BOTH PAGES. The realms and the contracts are the same data drawn two
// ways, so it is read here, where it outlives a switch between them, rather than by
// each page as it mounts — which read everything again on every switch.
export default function RealmsSection() {
    const { page, to, groupId, group } = usePlace();
    const data = useRealmsData(groupId, group);
    const location = useLocation();
    const asked = location.pathname.split('/').filter(Boolean).pop();
    const old = OLD_PAGES[asked];
    if (old) {
        const q = new URLSearchParams(location.search);
        Object.entries(old).forEach(([k, v]) => q.set(k, v));
        return <Navigate replace to={to('realms', 'profiles', q.toString())} />;
    }
    if (page?.id === 'profiles') return <AccessSection data={data} />;
    return <RealmsPage data={data} />;
}

// The page's realm editor and fork sheet, held where both the inspector and a
// right-click can open them.
function useRealmSheets(data, groupId, isAdmin, spaceName = null) {
    const [editing, setEditing] = useState(null);      // { realm|null, readOnly }
    const [forking, setForking] = useState(null);
    const toast = useToast();
    const calls = useMemo(() => realmsApi(groupId), [groupId]);
    const sheets = (
        <>
            {editing && (
                <RealmsEditor
                    realm={editing.realm}
                    readOnly={!!editing.readOnly}
                    base={bases(groupId).realms}
                    schemaNames={data.schemaNames}
                    schemaKeys={data.schemaKeys}
                    faces={data.faces}
                    mayEditFace={isAdmin}
                    groupId={groupId}
                    spaceName={groupId ? spaceName : null}
                    defaultFaceId={data.defaultFaceId}
                    loadTitles={calls.nodeTitles}
                    realms={data.realms}
                    onFacesChanged={data.reloadFaces}
                    onDone={(saved) => {
                        if (saved) toast.success(editing.realm ? `${editing.realm.name} is saved.` : 'The realm is made.');
                        setEditing(null);
                        if (saved) data.reload();
                    }}
                />
            )}
            {forking && (
                <NameModal
                    title={`Duplicate ${forking.name}`}
                    lead={`A new realm that covers the same. Nothing is shared from it until you add it on a consent page or a contract.${groupId && forking.profileNodeId ? ' The copy shows the group’s default identity.' : ''}`}
                    verb="Duplicate"
                    initial={`${forking.name} copy`}
                    onSubmit={async (name) => {
                        await calls.fork(forking, name);
                        toast.success(`${name} is made.`);
                        data.reload();
                    }}
                    onClose={() => setForking(null)}
                />
            )}
        </>
    );
    return { sheets, calls, toast, edit: (realm, readOnly) => setEditing({ realm, readOnly }), fork: setForking };
}

// the filter rail’s facets: what uses a realm, whether anything does, and which
// of the three kinds it is
const KIND_WORD = Object.fromEntries(KINDS.map((k) => [k.id, k.word]));
const REALM_FACETS = [
    { id: 'who', title: 'Used by', of: (e) => [...new Set(e.inside.map((p) => p.kind))], order: KINDS.map((k) => k.id), label: (k) => KIND_WORD[k] || k },
    { id: 'reach', title: 'In use', of: (e) => (e.inside.length ? 'some' : 'none'), order: ['some', 'none'], label: (k) => (k === 'some' ? 'Used' : 'Not used') },
    { id: 'sort', title: 'Kind', of: (e) => (e.realm.builtin ? 'builtin' : e.realm.system ? 'system' : e.realm.shared ? 'shared' : 'yours'), order: ['builtin', 'yours', 'shared', 'system'], label: (k) => ({ builtin: 'Built in', yours: 'Reusable', shared: 'For one recipient', system: 'Default' }[k]) },
];
// Who is on a group's realm, by name: "Priya, Tejas", "Mira, Jun, Daniel and 2 more".
export const whoOn = (given) => {
    if (!given.length) return 'No one';
    const names = given.map((m) => m.name);
    return names.length > 3 ? `${names.slice(0, 3).join(', ')} and ${names.length - 3} more` : names.join(', ');
};
const realmText = (e) => [e.realm.name, e.realm.description, ...e.inside.map((p) => p.label)].filter(Boolean).join(' ');

function RealmsPage({ data }) {
    const { section, page, groupId, isAdmin, spaceName, to, navigate } = usePlace();
    const [openId, open, close] = useOpen();
    const { sheets, calls, toast, edit, fork } = useRealmSheets(data, groupId, isAdmin, spaceName);
    // ?new=1 opens a new realm straight away: the consent page's way here when
    // none of your realms fit
    const location = useLocation();
    const asked = useRef(false);
    useEffect(() => {
        if (asked.current || !isAdmin || !data.loaded) return;
        if (new URLSearchParams(location.search).get('new') !== '1') return;
        asked.current = true;
        edit(null, false);
    }, [isAdmin, data.loaded, location.search, edit]);

    const ctx = (realm) => ({
        isAdmin, spaceName, inside: data.inside(realm), holding: data.holding?.(realm), calls, edit, fork, toast,
        onDeleted: () => { close(); data.reload(); },
    });
    const reachCtx = {
        isAdmin, spaceName, calls, toast, reload: data.reloadGrants,
        openGrant: (p) => navigate(to('realms', 'profiles', `open=${encodeURIComponent(p.key)}`)),
    };

    const realm = openId ? data.realms.find((r) => r.id === String(openId)) : null;
    // A realm a link names that the list does not hold: read on its own, in
    // this space. There and live, the list was older than the link (a realm
    // made a moment ago), so it is read again; otherwise say so.
    const linked = useLinkedItem(openId, {
        ready: data.loaded,
        inList: !!realm,
        read: async (id) => {
            const j = await (groupId ? api.request(`/groups/${groupId}/privacy-realms/${encodeURIComponent(id)}`) : api.getPrivacyRealm(id))
                .catch((e) => { if (e?.status === 404 || e?.status === 403) return null; throw e; });
            if (!j?.id || j.is_active === false) return null;
            data.reload();
            return j;
        },
    });
    const counted = data.realms.map((r) => ({ realm: r, inside: data.inside(r) }));
    const f = useFacets(counted, REALM_FACETS, { text: realmText, placeholder: 'Filter realms' });
    const keep = new Set(f.shown.map((e) => e.realm.id));
    // ONE LIST, every realm, sorted by how many active contracts it is shared into
    // (paused ones break a tie), most first. A default realm wears its tag; a realm
    // made for one recipient wears their face.
    const activeIn = (e) => e.inside.filter((p) => p.active).length;
    // a group's built-in realms lead: No access, then Full Access
    const builtinRank = (e) => ({ no_access: 0, full_access: 1 }[e.realm.builtin] ?? 2);
    const byActive = (a, b) => builtinRank(a) - builtinRank(b) || activeIn(b) - activeIn(a) || b.inside.length - a.inside.length || a.realm.name.localeCompare(b.realm.name);
    const sets = [
        {
            title: groupId ? `${spaceName}’s realms` : 'Your realms',
            list: [...counted].sort(byActive),
            empty: 'No realms yet. Make one to share less than everything.',
        },
    ];

    const noteOf = (r, inside) => {
        const active = inside.filter((p) => p.active).length;
        const paused = inside.length - active;
        const face = data.faceName(r.profileNodeId);
        // in a group: who is on it (admins on Full Access), then the contracts it is shared into
        if (groupId) {
            return [
                whoOn(data.givenTo(r)),
                inside.length ? `${plural(inside.length, 'contract')}${paused ? ` · ${paused} paused` : ''}` : null,
                face ? `as ${face}` : null,
            ].filter(Boolean).join(' · ');
        }
        const reach = !inside.length ? 'not used'
            : `${active} active${paused ? ` · ${paused} paused` : ''}`;
        return face ? `${reach} · as ${face}` : reach;
    };

    const cell = ({ realm: r, inside }) => {
        const who = r.shared ? recipientOf(r, data.profiles) : null;
        return (
            <Cell key={r.id} actions={realmActionsOf(r, ctx(r))}>
                <RealmCell
                    realm={r}
                    reach={inside.length}
                    tag={r.builtin ? 'BUILT IN' : r.system ? 'DEFAULT' : undefined}
                    {...(who ? { mark: <SealedMark size={56} mark={<RealmRings color={r.color} />} seal={<EntityFace profile={who} size={26} />} /> } : {})}
                    note={noteOf(r, inside)}
                    current={r.id === String(openId)}
                    onPress={() => open(r.id)}
                />
            </Cell>
        );
    };

    return (
        <NucleusPage
            section={section.label}
            title={page.label}
            count={data.loaded ? data.realms.length : undefined}
            verbs={<>
                <Verb primary icon={<Plus />} disabled={!isAdmin} title={isAdmin ? undefined : `Only an admin of ${spaceName} can make its realms`} onClick={() => edit(null, false)}>New realm</Verb>
            </>}
            inspector={realm && (
                <RealmInspector
                    key={realm.id}
                    realm={realm}
                    data={data}
                    calls={calls}
                    actions={realmActionsOf(realm, ctx(realm))}
                    reachActions={(p) => reachActionsOf(realm, p, reachCtx)}
                    onEdit={edit}
                    onClose={close}
                />
            )}
        >
            {data.error && <Empty error>{data.error}</Empty>}
            {linked?.state === 'missing' && <Note tone="live">{groupId ? `That realm isn’t one of ${spaceName}’s: it was deleted, or it isn’t one you can see.` : 'That realm isn’t one of yours: it was deleted, or it belongs to a group.'}</Note>}
            {linked?.state === 'error' && <Note tone="live">{linked.message || 'That realm could not be read.'}</Note>}
            {data.loaded && (<>
                <Faceted id="realms-all" rail={f.rail}>
                    <DeskTable top={0} between={40}>
                        {sets.map((s) => {
                            const shown = s.list.filter((e) => keep.has(e.realm.id));
                            if (!shown.length && (f.filtering || !s.empty)) return null;
                            return (
                                <ElementTable heads={{ note: 'Reach', tag: '' }} key={s.title} title={s.title} count={shown.length} note={s.note} empty={s.empty} className="nx-set-described">
                                    {shown.map(cell)}
                                </ElementTable>
                            );
                        })}
                        {f.filtering && !f.shown.length && <Empty>Nothing matches the filters.</Empty>}
                    </DeskTable>
                </Faceted>
            </>)}
            {sheets}
        </NucleusPage>
    );
}
